import { Box } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useGetExternalWorkersPoolDetail } from 'ogp/hooks/queryHooks/workersPools/use-get-external-workers-pool-detail';
import { useGetServiceAgreementsForCompany } from 'ogp/hooks/queryHooks/service-agreements/use-get-service-agreements-for-company';
import { useUpdateWorkersPool } from 'ogp/hooks/queryHooks/workersPools/use-update-workers-pool';
import { routes } from 'ogp/Routes';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Header, LwButton, Page, Toolbox, useFormButtons } from 'redesign';
import { RenderUnsettledUI } from 'shared/components';
import { useAlert } from 'shared/components/alerts';
import { useQueryParams } from 'shared/hooks/useQueryParams';
import { invariant } from 'shared/utils/utils';
import { ServiceAgreementsResponse } from 'ogp/services/service-agreements.types';
import { FlexPoolExternalForm } from '../flex-pool-external-create/flex-pool-external-form';
import { FlexPoolExternalFormData } from '../flex-pool-external-create/flex-pool-external.types';

const { queryParams: workersPoolEditQueryParams } = routes.WorkersPoolEdit;
const { queryParams: flexPoolsQueryParams } = routes.FlexPools;

export const WorkersPoolEditDataProvider = () => {
  const queryString = useQueryParams();
  const workersPoolId = queryString.get(workersPoolEditQueryParams.WORKERS_POOL_ID);
  invariant(workersPoolId);

  const poolQuery = useGetExternalWorkersPoolDetail(workersPoolId);
  const serviceAgreementsQuery = useGetServiceAgreementsForCompany({
    isLender: true,
    oppositeParty: 'COMPANY',
  });

  if (poolQuery.status !== 'success') {
    return <RenderUnsettledUI data={poolQuery} />;
  }

  if (serviceAgreementsQuery.status !== 'success') {
    return <RenderUnsettledUI data={serviceAgreementsQuery} />;
  }

  const defaultValues: FlexPoolExternalFormData = {
    name: poolQuery.data.name,
    description: poolQuery.data.description ?? '',
    serviceAgreements: poolQuery.data.serviceAgreements,
  };

  return (
    <WorkersPoolEditForm
      workersPoolId={workersPoolId}
      defaultValues={defaultValues}
      serviceAgreements={serviceAgreementsQuery.data}
    />
  );
};

const WorkersPoolEditForm = ({
  workersPoolId,
  defaultValues,
  serviceAgreements,
}: {
  workersPoolId: string;
  defaultValues: FlexPoolExternalFormData;
  serviceAgreements: ServiceAgreementsResponse;
}) => {
  const { t } = useTranslation(['ogp-screen-specific', 'web-common']);
  const generatePath = useCompanyRelativePath();
  const navigate = useNavigate();
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<FlexPoolExternalFormData>({ defaultValues });
  const updateMutation = useUpdateWorkersPool();
  const { submitButtonProps, cancelButtonProps } = useFormButtons(updateMutation.status);

  const navigateBackToPool = () => {
    const path = generatePath(routes.FlexPools);
    const params = new URLSearchParams();
    params.set(flexPoolsQueryParams.WORKERS_POOL_ID, workersPoolId);
    navigate(`${path}?${params}`);
  };

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      updateMutation.mutate(
        {
          workersPoolId,
          body: {
            name: data.name,
            description: data.description?.trim().length ? data.description : null,
            serviceAgreementIds: (data.serviceAgreements ?? []).map((s) => s.id),
          },
        },
        {
          onSuccess: () => {
            alertSuccess(t('flexpools.external.alerts.updated'));
            navigateBackToPool();
          },
          onError: (e) => alertError(e),
        }
      );
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={
          <Header titleText={t('flexpools.external_edit.title', { ns: 'ogp-screen-specific' })} />
        }
        toolbox={
          <Toolbox underline>
            <LwButton onClick={handleSubmit} iconColorMode="stroke" {...submitButtonProps}>
              {t('buttons.save', { ns: 'web-common' })}
            </LwButton>
            <LwButton onClick={navigateBackToPool} iconColorMode="fill" {...cancelButtonProps}>
              {t('buttons.cancel', { ns: 'web-common' })}
            </LwButton>
          </Toolbox>
        }
      >
        <Box width="100%">
          <FlexPoolExternalForm serviceAgreements={serviceAgreements} />
        </Box>
      </Page>
    </FormProvider>
  );
};
